/**
 * MaterialPanel Component (Simplified)
 * 
 * Panel untuk edit material yang ter-select:
 * - Color picker
 * - Metalness & Roughness sliders
 * - Opacity control
 * - Material type info
 */

import React from 'react'

export default function MaterialPanel({
  isOpen,
  onClose,
  selectedMaterial,
  onMaterialChange,
  saveSnapshot,
}) {
  if (!isOpen || !selectedMaterial) return null

  const handleColorChange = (e) => {
    if (selectedMaterial?.material?.color) {
      selectedMaterial.material.color.set(e.target.value)
      selectedMaterial.material.needsUpdate = true
      onMaterialChange({ ...selectedMaterial })
      saveSnapshot && saveSnapshot('Color Change')
    }
  }

  const handleMetalnessChange = (e) => {
    if (selectedMaterial?.material && selectedMaterial.material.metalness !== undefined) {
      selectedMaterial.material.metalness = parseFloat(e.target.value)
      selectedMaterial.material.needsUpdate = true
      onMaterialChange({ ...selectedMaterial })
    }
  }

  const handleRoughnessChange = (e) => {
    if (selectedMaterial?.material && selectedMaterial.material.roughness !== undefined) {
      selectedMaterial.material.roughness = parseFloat(e.target.value)
      selectedMaterial.material.needsUpdate = true
      onMaterialChange({ ...selectedMaterial })
    }
  }

  const handleOpacityChange = (e) => {
    if (selectedMaterial?.material) {
      const opacity = parseFloat(e.target.value)
      selectedMaterial.material.opacity = opacity
      selectedMaterial.material.transparent = opacity < 1
      selectedMaterial.material.needsUpdate = true
      onMaterialChange({ ...selectedMaterial })
    }
  }

  const handleEmissiveColorChange = (e) => {
    if (selectedMaterial?.material?.emissive) {
      selectedMaterial.material.emissive.set(e.target.value)
      selectedMaterial.material.needsUpdate = true
      onMaterialChange({ ...selectedMaterial })
      saveSnapshot && saveSnapshot('Emissive Color Change')
    }
  }

  const handleEmissiveIntensityChange = (e) => {
    if (selectedMaterial?.material && selectedMaterial.material.emissiveIntensity !== undefined) {
      selectedMaterial.material.emissiveIntensity = parseFloat(e.target.value)
      selectedMaterial.material.needsUpdate = true
      onMaterialChange({ ...selectedMaterial })
    }
  }

  const handleWireframeChange = (e) => {
    if (selectedMaterial?.material) {
      selectedMaterial.material.wireframe = e.target.checked
      selectedMaterial.material.needsUpdate = true
      onMaterialChange({ ...selectedMaterial })
      saveSnapshot && saveSnapshot('Wireframe Toggle')
    }
  }

  const handleMaterialSideChange = (e) => {
    if (selectedMaterial?.material && window.THREE_MODULE) {
      const THREE = window.THREE_MODULE
      const sideValue = parseInt(e.target.value)
      selectedMaterial.material.side = sideValue
      selectedMaterial.material.needsUpdate = true
      onMaterialChange({ ...selectedMaterial })
      saveSnapshot && saveSnapshot('Material Side Change')
    }
  }

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '100px',
        right: '20px',
        width: '300px',
        maxHeight: '500px',
        background: 'linear-gradient(135deg, rgba(20, 20, 30, 0.72) 0%, rgba(10, 10, 15, 0.78) 100%)',
        backdropFilter: 'blur(24px) saturate(190%)',
        WebkitBackdropFilter: 'blur(24px) saturate(190%)',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 16px 48px rgba(0, 0, 0, 0.12)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        border: '0.5px solid rgba(255, 255, 255, 0.18)',
        borderTop: '0.5px solid rgba(255, 255, 255, 0.25)',
        color: '#fff',
        zIndex: 100,
        animation: 'slideInFromRight 0.4s cubic-bezier(0.32, 0.72, 0, 1) forwards',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
          🎨 Material Editor
        </h3>
        <button
          onClick={onClose}
          style={{
            padding: '6px 10px',
            backgroundColor: 'rgba(239, 68, 68, 0.2)',
            color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          ✕
        </button>
      </div>

      {/* Material Info */}
      <div
        style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          fontSize: '12px',
        }}
      >
        <div style={{ fontWeight: 500, marginBottom: '4px' }}>
          {selectedMaterial.meshName || 'Selected Mesh'}
        </div>
        <div style={{ opacity: 0.7, fontSize: '11px' }}>
          {selectedMaterial.material?.type || 'Material'}
        </div>
      </div>

      {/* Color Picker */}
      {selectedMaterial.material?.color && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              fontSize: '12px',
              opacity: 0.7,
              display: 'block',
              marginBottom: '8px',
            }}
          >
            🎨 Base Color
          </label>
          <input
            type="color"
            value={`#${selectedMaterial.material.color.getHexString()}`}
            onChange={handleColorChange}
            onBlur={() => saveSnapshot && saveSnapshot('Color Change')}
            style={{
              width: '100%',
              height: '48px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          />
        </div>
      )}

      {/* Metalness Slider */}
      {selectedMaterial.material?.metalness !== undefined && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              fontSize: '12px',
              opacity: 0.7,
              display: 'block',
              marginBottom: '6px',
            }}
          >
            ⚙️ Metalness: {selectedMaterial.material.metalness.toFixed(2)}
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={selectedMaterial.material.metalness}
            onChange={handleMetalnessChange}
            onMouseUp={() => saveSnapshot && saveSnapshot('Metalness Change')}
            style={{
              width: '100%',
              accentColor: '#3b82f6',
            }}
          />
        </div>
      )}

      {/* Roughness Slider */}
      {selectedMaterial.material?.roughness !== undefined && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              fontSize: '12px',
              opacity: 0.7,
              display: 'block',
              marginBottom: '6px',
            }}
          >
            💎 Roughness: {selectedMaterial.material.roughness.toFixed(2)}
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={selectedMaterial.material.roughness}
            onChange={handleRoughnessChange}
            onMouseUp={() => saveSnapshot && saveSnapshot('Roughness Change')}
            style={{
              width: '100%',
              accentColor: '#3b82f6',
            }}
          />
        </div>
      )}

      {/* Opacity Slider */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            fontSize: '12px',
            opacity: 0.7,
            display: 'block',
            marginBottom: '6px',
          }}
        >
          👻 Opacity: {selectedMaterial.material?.opacity?.toFixed(2) || '1.00'}
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={selectedMaterial.material?.opacity || 1}
          onChange={handleOpacityChange}
          onMouseUp={() => saveSnapshot && saveSnapshot('Opacity Change')}
          style={{
            width: '100%',
            accentColor: '#3b82f6',
          }}
        />
      </div>

      {/* Emissive Color Picker */}
      {selectedMaterial.material?.emissive && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              fontSize: '12px',
              opacity: 0.7,
              display: 'block',
              marginBottom: '8px',
            }}
          >
            💡 Emissive Color
          </label>
          <input
            type="color"
            value={`#${selectedMaterial.material.emissive.getHexString()}`}
            onChange={handleEmissiveColorChange}
            style={{
              width: '100%',
              height: '48px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          />
        </div>
      )}

      {/* Emissive Intensity Slider */}
      {selectedMaterial.material?.emissiveIntensity !== undefined && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              fontSize: '12px',
              opacity: 0.7,
              display: 'block',
              marginBottom: '6px',
            }}
          >
            ✨ Emissive Intensity: {selectedMaterial.material.emissiveIntensity.toFixed(2)}
          </label>
          <input
            type="range"
            min="0"
            max="5"
            step="0.1"
            value={selectedMaterial.material.emissiveIntensity}
            onChange={handleEmissiveIntensityChange}
            onMouseUp={() => saveSnapshot && saveSnapshot('Emissive Intensity Change')}
            style={{
              width: '100%',
              accentColor: '#f59e0b',
            }}
          />
        </div>
      )}

      {/* Wireframe Toggle */}
      {selectedMaterial.material?.wireframe !== undefined && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '10px',
              backgroundColor: selectedMaterial.material.wireframe
                ? 'rgba(59, 130, 246, 0.15)'
                : 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: selectedMaterial.material.wireframe
                ? '1px solid rgba(59, 130, 246, 0.4)'
                : '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <input
              type="checkbox"
              checked={selectedMaterial.material.wireframe}
              onChange={handleWireframeChange}
              style={{
                marginRight: '10px',
                width: '16px',
                height: '16px',
                cursor: 'pointer',
                accentColor: '#3b82f6',
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: 500 }}>
              📐 Wireframe Mode
            </span>
          </label>
        </div>
      )}

      {/* Material Side Selector */}
      {selectedMaterial.material?.side !== undefined && window.THREE_MODULE && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              fontSize: '12px',
              opacity: 0.7,
              display: 'block',
              marginBottom: '8px',
            }}
          >
            🔄 Material Side
          </label>
          <select
            value={selectedMaterial.material.side}
            onChange={handleMaterialSideChange}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#1e1e23',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              fontSize: '13px',
              cursor: 'pointer',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
          >
            <option value={0}>Front Side</option>
            <option value={1}>Back Side</option>
            <option value={2}>Double Side</option>
          </select>
        </div>
      )}

      {/* Info Text */}
      <div
        style={{
          marginTop: '16px',
          padding: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '6px',
          fontSize: '10px',
          opacity: 0.6,
          lineHeight: '1.5',
        }}
      >
        💡 Click pada mesh di viewport untuk select material. Ctrl+Z untuk undo changes.
      </div>
    </div>
  )
}

